import type { CSSProperties, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './chat-pacing';

export type PanelPhase = 'docked' | 'opening' | 'open' | 'closing';

type Rect = { top: number; left: number; width: number; height: number };

type UsePanelOverlayParams = {
  /** Mount already full screen, for the /auth/signup deep link. Skips the animation. */
  startOpen: boolean;
  /** Fires once the panel is back in its slot, after the close animation. */
  onClosed?: () => void;
};

const FADE_MS = 200;
const MOVE_MS = 580;
const EASE = 'cubic-bezier(.65, 0, .25, 1)';
const MOVE = ['top', 'left', 'width', 'height'].map((prop) => `${prop} ${MOVE_MS}ms ${EASE}`).join(', ');

// 10px matches the page gutter, so the expanded panel sits where the docked one
// sits relative to the viewport edge.
const FULL: CSSProperties = { top: 10, left: 10, width: 'calc(100vw - 20px)', height: 'calc(100vh - 20px)' };

const measure = (ref: RefObject<HTMLDivElement | null>): Rect | null => {
  const box = ref.current?.getBoundingClientRect();
  return box ? { top: box.top, left: box.left, width: box.width, height: box.height } : null;
};

/**
 * Escape is also how people dismiss an autofill list, a password manager popup or an
 * IME composition, and from inside a field it must do only that. Treating it as
 * "close the signup screen" threw away a half filled form, passwords included.
 */
const isEscapeForSomethingElse = (ev: KeyboardEvent): boolean => {
  if (ev.defaultPrevented || ev.isComposing) return true;
  const target = ev.target;
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

/**
 * Moves the chat panel between its docked slot and full screen.
 *
 * The panel is a normal flex child when docked. To open it, the slot keeps its
 * size in the layout (so nothing reflows) while the panel inside it goes
 * position: fixed at the slot's measured rect and then animates to cover the
 * viewport. Closing is the same in reverse. Animating the real top/left/width/
 * height rather than a transform is what lets the panel's contents re-lay-out at
 * full size instead of being scaled.
 *
 * Contents fade out before the move and back in after it, so the thread is never
 * visibly reflowing mid-flight. Under prefers-reduced-motion every duration is zero
 * and there is no transition, so it simply switches.
 *
 * It is modal while it is open: focus moves into it and comes back to whatever
 * opened it, and `setLocked(true)` makes it refuse to close (used while an account
 * is being created, when closing would hide the outcome).
 */
export const usePanelOverlay = ({ startOpen, onClosed }: UsePanelOverlayParams) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const [phase, setPhase] = useState<PanelPhase>(startOpen ? 'open' : 'docked');
  const [expanded, setExpanded] = useState(startOpen);
  const [rect, setRect] = useState<Rect | null>(null);
  const [contentVisible, setContentVisible] = useState(true);
  const [reduced] = useState(prefersReducedMotion);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // The phase only changes after the fade timer, so a second click or Escape inside
  // those 200ms would otherwise queue a second sequence, and onClosed would fire twice
  // (on /auth/signup and /auth/verify that means navigating twice).
  const moving = useRef(false);
  const locked = useRef(false);
  const trigger = useRef<HTMLElement | null>(null);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(setTimeout(fn, reduced ? 0 : ms));
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const open = () => {
    if (phase !== 'docked' || moving.current) return;
    moving.current = true;
    trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setContentVisible(false);

    later(() => {
      setRect(measure(slotRef));
      setPhase('opening');

      if (reduced) {
        // No transition to run, so there is no docked frame to paint first. Setting
        // this a couple of frames later left the panel sitting open at its small
        // size before jumping to full screen.
        setExpanded(true);
      } else {
        setExpanded(false);
        // Two frames. The first paints the fixed panel at its docked rect; the second
        // changes its target, which is what gives the browser a transition to run.
        requestAnimationFrame(() => requestAnimationFrame(() => setExpanded(true)));
      }

      later(() => {
        setPhase('open');
        setContentVisible(true);
        moving.current = false;
      }, MOVE_MS + 40);
    }, FADE_MS);
  };

  const close = () => {
    if (phase !== 'open' || moving.current || locked.current) return;
    moving.current = true;
    setContentVisible(false);

    later(() => {
      setRect(measure(slotRef));
      setPhase('closing');
      setExpanded(false);

      later(() => {
        setPhase('docked');
        setContentVisible(true);
        moving.current = false;
        onClosed?.();
      }, MOVE_MS);
    }, FADE_MS);
  };

  const setLocked = (value: boolean) => {
    locked.current = value;
  };

  useEffect(() => {
    if (phase !== 'open') return;
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && !isEscapeForSomethingElse(ev)) close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase]);

  // Focus follows the panel. The form column goes inert while it is open, so focus
  // that was on a button in it would otherwise fall to <body>, and nothing would
  // bring it back. Skipped when something inside the panel has already taken focus
  // (an autofocused button), which is the better place for it.
  useEffect(() => {
    const panel = panelRef.current;
    if (phase === 'open' && panel && !panel.contains(document.activeElement)) panel.focus();
    if (phase === 'docked') {
      trigger.current?.focus();
      trigger.current = null;
    }
  }, [phase]);

  const isMoving = phase === 'opening' || phase === 'closing';
  const target: CSSProperties | undefined = expanded ? FULL : (rect ?? undefined);

  const asideStyle: CSSProperties | undefined =
    phase === 'docked' ? undefined : { ...target, transition: isMoving && !reduced ? MOVE : 'none' };

  return { slotRef, panelRef, phase, contentVisible, asideStyle, open, close, setLocked };
};

export type PanelOverlay = ReturnType<typeof usePanelOverlay>;
